import { ApiRequestError } from '@deskworks/client';
import { PASSWORD_MIN_LENGTH } from '@deskworks/domain';
import { Banner, Button, Logo, LogoMark, Spinner, TextField } from '@deskworks/ui';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { desktop } from '../app/platform.js';
import { safeNext } from '../app/router.js';
import { api, useSession } from '../app/session.js';
import { localTimeZone } from '../lib/format.js';

function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <div className="dw-auth">
      <div className="dw-auth__brand">
        <Logo tone="white" size={28} />
        <p className="dw-auth__tagline">Your work. In one place.</p>
        <ul className="dw-auth__points" aria-label="What DeskWorks One brings together">
          <li>Every mail account in one inbox</li>
          <li>Calendars with built-in meeting links</li>
          <li>Persistent team chat</li>
          <li>Meetings without another app</li>
        </ul>
      </div>
      <main className="dw-auth__panel">
        <div className="dw-auth__card">
          <span className="dw-auth__mark">
            <LogoMark size={36} />
          </span>
          <h1>{title}</h1>
          {subtitle ? <p className="dw-auth__subtitle">{subtitle}</p> : null}
          {children}
        </div>
      </main>
    </div>
  );
}

function errorText(err: unknown) {
  if (err instanceof ApiRequestError) {
    if (err.code === 'validation' && Array.isArray(err.details)) {
      return (err.details as { path: string; message: string }[]).map((d) => `${d.path}: ${d.message}`).join(' · ');
    }
    return err.message;
  }
  return 'Something went wrong. Try again.';
}

export function SignInPage() {
  const { status, setSession, endedReason } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [needsSetup, setNeedsSetup] = useState(false);
  const next = safeNext(params.get('next'));

  useEffect(() => {
    api.authStatus().then((s) => setNeedsSetup(s.needsSetup), () => undefined);
  }, []);

  if (status === 'signed-in') return <Navigate to={next} replace />;
  if (needsSetup) return <Navigate to="/setup" replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await api.signIn({ email, password });
      setPassword('');
      setSession(s);
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Sign in" subtitle="Use your DeskWorks One account. Mail accounts are connected separately after you sign in.">
      {endedReason ? <Banner tone="info">{endedReason}</Banner> : null}
      {error ? (
        <Banner tone="danger">
          <span role="alert">{error}</span>
        </Banner>
      ) : null}
      <form onSubmit={submit} className="dw-auth__form" noValidate>
        <TextField label="Email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
        <TextField label="Password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <Button type="submit" variant="primary" size="lg" busy={busy} className="dw-auth__submit">
          Sign in
        </Button>
      </form>
      <p className="dw-auth__fine">Joining a meeting as a guest? Use the meeting link you were sent — no account needed.</p>
      {desktop ? null : (
        <p className="dw-auth__fine">
          <Link to="/download">Get the desktop app for Windows and Mac</Link>
        </p>
      )}
      <p className="dw-auth__fine">
        <a href="/privacy.html">Privacy policy</a> · <a href="/terms.html">Terms of service</a>
      </p>
    </AuthLayout>
  );
}

export function SetupPage() {
  const { setSession, status } = useSession();
  const navigate = useNavigate();
  const [form, setForm] = useState({ displayName: '', email: '', password: '', workspaceName: '', setupCode: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [codeRequired, setCodeRequired] = useState(false);

  useEffect(() => {
    api.authStatus().then(
      (s) => {
        setAllowed(s.needsSetup);
        setCodeRequired(!!s.setupCodeRequired);
      },
      () => setAllowed(false),
    );
  }, []);

  if (status === 'signed-in') return <Navigate to="/mail" replace />;
  if (allowed === null) return <div className="dw-loading"><Spinner size={28} label="Loading" /></div>;
  if (!allowed) return <Navigate to="/signin" replace />;

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { setupCode, ...rest } = form;
      const s = await api.setup({ ...rest, timeZone: localTimeZone(), ...(codeRequired ? { setupCode } : {}) });
      setSession(s);
      navigate('/settings/accounts?welcome=1', { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Set up DeskWorks One" subtitle="Create the owner account and your first workspace. You can invite your team next.">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      <form onSubmit={submit} className="dw-auth__form">
        {codeRequired ? (
          <TextField label="Setup code" required value={form.setupCode} onChange={set('setupCode')} autoComplete="one-time-code" hint="The one-time code from whoever installed this server." autoFocus />
        ) : null}
        <TextField label="Your name" autoComplete="name" required value={form.displayName} onChange={set('displayName')} autoFocus={!codeRequired} />
        <TextField
          label="Sign-in email"
          type="email"
          autoComplete="email"
          required
          value={form.email}
          onChange={set('email')}
          hint="Only your DeskWorks One login (and how teammates invite you). It doesn't connect a mailbox — you'll add Gmail, Outlook, Zoho or Spacemail accounts next, as many as you like."
        />
        <TextField label="Password" type="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH} value={form.password} onChange={set('password')} hint={`At least ${PASSWORD_MIN_LENGTH} characters.`} />
        <TextField label="Workspace name" required value={form.workspaceName} onChange={set('workspaceName')} placeholder="Product team" />
        <Button type="submit" variant="primary" size="lg" busy={busy} className="dw-auth__submit">
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}

export function AcceptInvitePage() {
  const { token = '' } = useParams();
  const { status, setSession, refresh } = useSession();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof api.invitationPreview>> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState({ displayName: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.invitationPreview(token).then(setPreview, (err) => setLoadError(errorText(err)));
  }, [token]);

  if (loadError) {
    return (
      <AuthLayout title="Invitation unavailable">
        <Banner tone="warning">This invitation link is not valid. Ask the person who invited you for a new link.</Banner>
        <p>
          <Link to="/signin">Go to sign in</Link>
        </p>
      </AuthLayout>
    );
  }
  if (!preview) return <div className="dw-loading"><Spinner size={28} label="Loading" /></div>;
  if (preview.expired || preview.accepted) {
    return (
      <AuthLayout title="Invitation unavailable">
        <Banner tone="warning">{preview.accepted ? 'This invitation was already used.' : 'This invitation has expired.'} Ask {preview.invitedBy} for a new link.</Banner>
      </AuthLayout>
    );
  }

  async function acceptAsNew(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await api.acceptInvite({ token, ...form, timeZone: localTimeZone() });
      setSession(s);
      navigate('/chat', { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function acceptSignedIn() {
    setBusy(true);
    setError(null);
    try {
      await api.acceptInviteSignedIn(token);
      await refresh();
      navigate('/chat', { replace: true });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title={`Join ${preview.workspaceName}`} subtitle={`${preview.invitedBy} invited ${preview.email} to DeskWorks One.`}>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {status === 'signed-in' ? (
        <Button variant="primary" size="lg" busy={busy} onClick={acceptSignedIn} className="dw-auth__submit">
          Join workspace
        </Button>
      ) : preview.accountExists ? (
        <>
          <p>You already have a DeskWorks One account. Sign in to accept this invitation.</p>
          <Button variant="primary" size="lg" onClick={() => navigate(`/signin?next=${encodeURIComponent(`/invite/${token}`)}`)} className="dw-auth__submit">
            Sign in to accept
          </Button>
        </>
      ) : (
        <form onSubmit={acceptAsNew} className="dw-auth__form">
          <TextField label="Email" value={preview.email} readOnly disabled />
          <TextField label="Your name" autoComplete="name" required value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} autoFocus />
          <TextField label="Create a password" type="password" autoComplete="new-password" minLength={PASSWORD_MIN_LENGTH} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} hint={`At least ${PASSWORD_MIN_LENGTH} characters.`} />
          <Button type="submit" variant="primary" size="lg" busy={busy} className="dw-auth__submit">
            Create account and join
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
